import {Component, OnInit, Optional, QueryList, ViewChildren} from '@angular/core';
import {EtlExportUpdateComponent} from '../etl-export-update.component';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MessageService, SelectItemGroup} from 'primeng/api';
import {
    AbstractStoreConf,
    ConfiguratorDatastoreService,
    ConfiguratorEtlService,
    EtlExportParamDTO,
    FileProcessingLoadConf,
    ScriptProcessingLoadConf
} from '@tj/tj-dmp-lib-api';
import {FileServerConfigComponent} from "../../../../components/file-server-config/file-server-config.component";

@Component({
    selector: 'dmp-etl-export-update-param',
    templateUrl: './etl-export-update-param.component.html',
    styleUrls: ['./etl-export-update-param.component.scss'],
    standalone: false
})
export class EtlExportUpdateParamComponent implements OnInit {
    public form: FormGroup;
    public formats: SelectItemGroup[] = [];
    private readonly trustPilotTypeLoad = 'TrustPilotProcessingLoadConf';
    private readonly trustPilotFormat = 'TRUSTPILOT';

    public fileConf: FileProcessingLoadConf;
    /** Affiliés du job : ils déterminent les connexions enregistrées proposées. */
    public jobPartners: string[] = [];
    /** Export du tenant : connexions du tenant, et d'un affilié seulement pour un canal qui ne porte que ses données. */
    public tenantExport = false;
    public separateExport = false;
    public exportPartners: string[] = [];
    public allowedGrouped: (string | null)[] = [null];
    public allowedByPartner: Record<string, (string | null)[]> = {};
    public readonly allowedTenantOnly: (string | null)[] = [null];
    /** Choix de l'affilié d'un canal : « Nom [CODE] », comme les autres listes d'affiliés. */
    public exportPartnerOptions: { label: string; value: string }[] = [];

    @ViewChildren(FileServerConfigComponent) fileServers!: QueryList<FileServerConfigComponent>;

//    fileServers: any = [];

    constructor(@Optional() public parent: EtlExportUpdateComponent,
                private readonly route: ActivatedRoute,
                private readonly router: Router,
                private readonly translate: TranslateService,
                private fb: FormBuilder,
                private readonly configuratorEtlService: ConfiguratorEtlService,
                private readonly configuratorDatastoreService: ConfiguratorDatastoreService,
                protected readonly messageService: MessageService) {

        this.formats = [
            {
                label: 'Fichier',
                items: [
                    {label: 'CSV', value: 'CSV'},
                    {label: 'PDF', value: 'PDF'},
                    {label: 'XLSX', value: 'XLSX'}
                ]
            },
            {
                label: 'Autre',
                items: [
                    {label: 'Script', value: 'groovy'},
                    {label: 'TrustPilot', value: this.trustPilotFormat}
                ]
            }
        ];
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            format: [this.formats[0].items[0].value, [Validators.required]],
            cron: [''],
            groovy: [''],
            slackNotificationUrl: [''],
            trustpilotSenderEmail: [''],
            trustpilotTemplateId: [''],
            trustpilotTags: [[]],
            enableScheduling: [false]
        });

        // Gérer l'activation/désactivation du champ CRON
        this.form.get('enableScheduling').valueChanges.subscribe(enabled => {
            if (enabled) {
                this.form.get('cron').enable();
            } else {
                this.form.get('cron').disable();
                // Clear stores when scheduling is disabled
                if (this.fileConf && this.fileConf.stores) {
                    this.fileConf.stores = [];
                }
            }
        });

        this.form.get('format').valueChanges.subscribe(format => {
            this.configureTrustPilotValidators(format);

            if (format === this.trustPilotFormat) {
                this.fileConf = null;
                return;
            }

            if (format !== 'groovy') {
                // Initialize fileConf if doesn't exist
                if (!this.fileConf) {
                    this.fileConf = {
                        typeLoad: 'FileProcessingLoadConf',
                        type: format,
                        stores: []
                    } as FileProcessingLoadConf;
                } else if (this.fileConf.typeLoad === 'FileProcessingLoadConf') {
                    this.fileConf.type = format;
                }
            } else {
                this.fileConf = null;
            }
        });

        this.parent.etl$.subscribe(etl => {
            this.jobPartners = etl ? [...new Set([...(etl.partner ? [etl.partner] : []), ...(etl.personalDataAccessPartners ?? [])])] : [];
            this.tenantExport = !!etl && !etl.partner;
            this.separateExport = etl?.exportMode === 'SEPARATE';
            this.exportPartners = etl?.exportPartners ?? [];
            this.allowedGrouped = this.exportPartners.length === 1 ? [null, this.exportPartners[0]] : [null];
            this.allowedByPartner = {};
            this.exportPartners.forEach(p => this.allowedByPartner[p] = [null, p]);
            this.exportPartnerOptions = this.exportPartners.map(p => ({label: p, value: p}));
            if (this.tenantExport && this.separateExport && this.exportPartners.length > 0) {
                this.configuratorDatastoreService.getDatastoreUsingGET(this.parent.tenant, true).subscribe(datastore => {
                    const names = (datastore?.partners ?? []).reduce((acc, p) => ({...acc, [p.code]: p.name}), {} as Record<string, string>);
                    this.exportPartnerOptions = this.exportPartners.map(p => ({label: names[p] ? `${names[p]} [${p}]` : p, value: p}));
                });
            }
            if (etl && etl.processingLoad) {
                let format = null;
                let script = null;
                let enableScheduling = false;
                let slackNotificationUrl = '';
                let trustpilotSenderEmail = '';
                let trustpilotTemplateId = '';
                let trustpilotTags = [];

                if (etl.processingLoad.typeLoad === 'FileProcessingLoadConf') {
                    this.fileConf = (etl.processingLoad) as FileProcessingLoadConf;
                    format = this.fileConf.type;
                    // Déterminer si la planification est activée
                    enableScheduling = !!(etl.schedulers || (this.fileConf.stores && this.fileConf.stores.length > 0));
                } else if (etl.processingLoad.typeLoad === 'ScriptProcessingLoadConf') {
                    const scriptConf = (etl.processingLoad) as ScriptProcessingLoadConf;
                    format = 'groovy';
                    script = scriptConf.scriptName;
                    enableScheduling = !!etl.schedulers;
                } else if (etl.processingLoad.typeLoad === this.trustPilotTypeLoad) {
                    format = this.trustPilotFormat;
                    slackNotificationUrl = etl.processingLoad['slackNotificationUrl'] || '';
                    trustpilotSenderEmail = etl.processingLoad['trustpilotSenderEmail'] || '';
                    trustpilotTemplateId = etl.processingLoad['trustpilotTemplateId'] || '';
                    trustpilotTags = (etl.processingLoad['trustpilotTags'] || []) as string[];
                    enableScheduling = !!etl.schedulers;
                    this.fileConf = null;
                }

                this.form.patchValue({
                    format,
                    cron: etl.schedulers,
                    groovy: script,
                    slackNotificationUrl,
                    trustpilotSenderEmail,
                    trustpilotTemplateId,
                    trustpilotTags,
                    enableScheduling
                });
                this.configureTrustPilotValidators(format);
            } else if (etl) {
                // Initialize fileConf for new job
                if (this.form.get('format').value !== 'groovy') {
                    this.fileConf = {
                        typeLoad: 'FileProcessingLoadConf',
                        type: this.form.get('format').value,
                        stores: []
                    } as FileProcessingLoadConf;
                }
            }
        });
    }

    saveStep() {
        const enableScheduling = this.form.get('enableScheduling').value;
        const selectedFormat = this.form.get('format').value;
        const groovyValue = this.form.value.groovy;
        const hasChannels = this.fileConf && this.fileConf.stores && this.fileConf.stores.length > 0;
        const isTrustPilot = selectedFormat === this.trustPilotFormat;

        if (isTrustPilot) {
            this.form.get('trustpilotSenderEmail').markAsTouched();
            this.form.get('trustpilotTemplateId').markAsTouched();
            if (this.form.get('trustpilotSenderEmail').invalid || this.form.get('trustpilotTemplateId').invalid) {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('etl.export.param.trustpilotFieldsInvalid')
                });
                return;
            }
        }

        // Valider les canaux uniquement si la planification est activée
        if (!isTrustPilot && enableScheduling && hasChannels) {
            const invalid = this.fileServers.filter(f => !f.isValid());
            invalid.forEach(f => f.showErrors());
            const error = invalid.length > 0;
            if (error) {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('etl.export.param.formsInvalid')
                });
                return;
            }
        }

        if (!isTrustPilot && this.fileConf && enableScheduling && !hasChannels && !this.separateExport) {
            this.showError('etl.export.param.channelRequired');
            return;
        }

        if (!isTrustPilot && enableScheduling && this.tenantExport && this.separateExport) {
            const stores = this.fileConf?.stores ?? [];
            if (stores.some(store => !this.exportPartners.includes(store.partner))) {
                this.showError('etl.export.param.channelPartnerMissing');
                return;
            }
            const uncovered = this.exportPartners.filter(p => !stores.some(store => store.partner === p));
            if (uncovered.length > 0) {
                this.showError('etl.export.param.channelMissingForPartner', {partners: this.uncoveredSummary});
                return;
            }
        }

        // Construire le DTO
        const etl: any = {
            id: this.parent.id,
            format: selectedFormat,
            groovyName: typeof groovyValue === 'string' ? groovyValue : groovyValue?.value,
            // Save cron if scheduling is enabled, regardless of channels
            cron: enableScheduling ? this.form.get('cron').value : null
        };

        // Only send stores if there are channels configured
        // If scheduling is enabled but no channels, send empty array to prevent schedule from starting
        if (!isTrustPilot && enableScheduling) {
            etl.stores = hasChannels
                ? this.fileServers.map((s, i) => ({...s.getStore(), partner: this.separateExport ? this.fileConf.stores[i].partner : undefined}))
                : [];
        }
        if (isTrustPilot) {
            etl.slackNotificationUrl = this.form.value.slackNotificationUrl || null;
            etl.trustpilotSenderEmail = this.form.value.trustpilotSenderEmail;
            etl.trustpilotTemplateId = this.form.value.trustpilotTemplateId;
            etl.trustpilotTags = (this.form.value.trustpilotTags || [])
                .map(tag => (tag || '').trim())
                .filter(tag => !!tag);
        } else {
            etl.slackNotificationUrl = null;
            etl.trustpilotSenderEmail = null;
            etl.trustpilotTemplateId = null;
            etl.trustpilotTags = null;
        }

        this.configuratorEtlService.updateExportParam(etl as EtlExportParamDTO, this.parent.tenant).subscribe(res => {
            this.parent.goToList();
        });
    }

    /** Nouveau canal sans type : S3 ou FTP selon la connexion choisie dans le canal. */
    public addChannel(): void {
        if (this.fileConf.stores == null) {
            this.fileConf.stores = [];
        }
        const partner = this.separateExport
            ? this.exportPartners.find(p => !this.fileConf.stores.some(store => store.partner === p)) ?? this.exportPartners[0]
            : undefined;
        this.fileConf.stores.push({partner} as AbstractStoreConf);
    }

    public readonly maxListedPartners = 5;

    /** Liste courte des affiliés sans canal : les premiers, puis « +N » (liste complète en infobulle). */
    public get uncoveredSummary(): string {
        const missing = this.uncoveredPartners;
        const listed = missing.slice(0, this.maxListedPartners).join(', ');
        return missing.length > this.maxListedPartners ? `${listed} +${missing.length - this.maxListedPartners}` : listed;
    }

    /** Mode séparé : affiliés de l'export qui n'ont encore aucun canal. */
    public get uncoveredPartners(): string[] {
        const stores = this.fileConf?.stores ?? [];
        return this.exportPartners.filter(p => !stores.some(store => store.partner === p));
    }

    public updateStorePartner(index: number, partner: string): void {
        this.fileConf.stores[index].partner = partner;
    }

    private showError(key: string, params?: object): void {
        this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('datastore.errorSummary'),
            detail: this.translate.instant(key, params)
        });
    }

    public removeStore(index: number): void {
        this.fileConf.stores.splice(index, 1);
    }

    public updateStoreChange($event: AbstractStoreConf, index: number) {
        this.fileConf.stores[index] = {...$event, partner: this.fileConf.stores[index]?.partner};
    }

    public isTrustPilotSelected(): boolean {
        return this.form?.get('format')?.value === this.trustPilotFormat;
    }

    private configureTrustPilotValidators(format: string): void {
        const emailControl = this.form.get('trustpilotSenderEmail');
        const templateControl = this.form.get('trustpilotTemplateId');
        const isTrustPilot = format === this.trustPilotFormat;

        if (isTrustPilot) {
            emailControl.setValidators([Validators.required, Validators.email]);
            templateControl.setValidators([Validators.required]);
        } else {
            this.clearTrustPilotFields();
            emailControl.clearValidators();
            templateControl.clearValidators();
        }

        emailControl.updateValueAndValidity({emitEvent: false});
        templateControl.updateValueAndValidity({emitEvent: false});
    }

    private clearTrustPilotFields(): void {
        this.form.patchValue({
            slackNotificationUrl: '',
            trustpilotSenderEmail: '',
            trustpilotTemplateId: '',
            trustpilotTags: []
        }, {emitEvent: false});
    }
}
